/**
 * Web staff permission gating for the dashboard.
 *
 * A store owner is unrestricted when any staff row for the active store has
 * role "Owner". Invited staff carry the permission tree of
 * `utils/webPermissions.js` (module → group → action); the nav, the routes,
 * the pipeline steps and every action inside them are gated against it. The
 * API may return several rows per store — Owner must win.
 *
 * Two levels of check, one source — both take EXACT leaves, never a group:
 *   can(user, slug, 'orders.livre.encaisser')     one action
 *   canAny(user, slug, ['staff.general.view'])    any one of several
 */

import { isPackPathAllowed } from './subscriptionPlan';
import { normalizeWebPermissions, isAllowed } from './webPermissions';

// Path prefix -> the exact actions that grant access (any one is enough).
//
// Reaching a screen is `view`, never "holds some right in that area". A group
// scope would let a write right stand in for the read one: `products.general`
// was satisfied by `create` alone, so a staff with view=false, create=true saw
// Produits in the nav. The `view` invariant in webPermissions.js kills that
// state at the source; these rules name the action so nothing can drift back.
//
// More specific prefixes MUST come first (first match wins).
const ROUTE_RULES = [
  ['/orders/abandoned', ['orders.abandoned.view']],
  ['/orders/refunded',  ['orders.pos.refund', 'orders.livre.refund']],
  // Any pipeline step is a way in; which tabs render is decided inside the
  // page. `orders.general` is deliberately absent — create/export/print alone
  // would open a page with no visible step.
  ['/orders',           ['orders.confirmation.view', 'orders.pre_expedition.view',
                         'orders.preparation.view', 'orders.pos.view',
                         'orders.retour.view', 'orders.livre.view']],
  ['/products',         ['products.general.view']],
  ['/discounts',        ['discounts.general.view']],
  ['/customers',        ['customers.general.view']],
  // Two ways in: the team admin, and the bookkeeper who only manages pay.
  // Payroll has no `view` — `manage` is its only action.
  ['/staff',            ['staff.general.view', 'payroll.general.manage']],
  ['/marketing',        ['marketing.general.view']],
  ['/analytics',        ['analytics.general.view']],
  ['/channels',         ['channels.general.view']],
  ['/wallet',           ['wallet.general.view']],
  ['/themes',           ['themes.general.view']],
  // Funnels are landing themes of the store: same right as the themes.
  ['/funnels',          ['themes.general.view']],
  ['/shipping',         ['shipping.general.view']],
  ['/tracking',         ['shipping.general.view']],
  ['/purchases',        ['purchases.general.view']],
  ['/affiliate',        ['affiliate.general.view']],
  // Store-wide file library, not a personal drive: it belongs to whoever
  // administers the store.
  ['/personal-space',   ['settings.general.view']],
];

// Account / utility pages everyone can always reach. `/my-earnings` belongs
// here rather than behind a payroll key: it shows the caller their own balance
// and nothing else — the API answers it from the session, never from a staff
// id in the request — so gating it would only ever lock someone out of their
// own pay.
const ALWAYS_ALLOWED = ['/profile', '/support', '/notifications', '/activities',
  '/my-earnings'];

// Order pipeline steps (Orders.js STEP values) -> their group in the
// permission tree. The step tab is gated on `<group>.view`, its buttons on
// the other actions of the same group.
export const ORDER_STEP_GROUP = {
  0: 'confirmation',
  1: 'pre_expedition',
  2: 'preparation',
  3: 'pos',
  4: 'retour',
  5: 'livre',
};

function staffRole(staff) {
  return staff?.role ?? staff?.role_name ?? '';
}

function isOwnerRole(role) {
  return role === 'Owner' || role === 'owner';
}

function storeStaffMemberships(user, storeSlug) {
  if (!user || !storeSlug) return [];
  return (user.staff || []).filter((s) => s.storeSlug === storeSlug);
}

function hasOwnerMembership(user, storeSlug) {
  return storeStaffMemberships(user, storeSlug).some((s) => isOwnerRole(staffRole(s)));
}

/** True when the active user owns the store (staff memberships aside). */
export function isStoreOwner(user, storeSlug) {
  return hasOwnerMembership(user, storeSlug);
}

/** Prefer the Owner row when the API returns multiple memberships for one store. */
export function getCurrentStaff(user, storeSlug) {
  const memberships = storeStaffMemberships(user, storeSlug);
  if (!memberships.length) return null;
  return memberships.find((s) => isOwnerRole(staffRole(s))) || memberships[0];
}

/** True when the active user is an invited staff (so gating applies). */
export function isRestrictedStaff(user, storeSlug) {
  const memberships = storeStaffMemberships(user, storeSlug);
  if (!memberships.length) return false;
  return !hasOwnerMembership(user, storeSlug);
}

/**
 * The permission tree of this user for this store, already normalised (the
 * old flat shape is expanded on the way in). Several membership rows can
 * exist for one store; a right held by any of them counts.
 */
function permissionTrees(user, storeSlug) {
  return storeStaffMemberships(user, storeSlug)
    .map((s) => normalizeWebPermissions(s.permissions));
}

/**
 * One exact right: `can(user, slug, 'orders.confirmation.cancel')`.
 * This is the check to use for anything a staff member can DO.
 */
export function can(user, storeSlug, path) {
  if (hasOwnerMembership(user, storeSlug)) return true;
  return permissionTrees(user, storeSlug).some((tree) => isAllowed(tree, path));
}

/** A pipeline step is reachable when its `view` action is granted. */
export function canOrderStep(user, storeSlug, step) {
  const group = ORDER_STEP_GROUP[step];
  return group ? can(user, storeSlug, `orders.${group}.view`) : false;
}

/** Any one of several exact actions — the nav and the route guard use this. */
export function canAny(user, storeSlug, paths) {
  if (hasOwnerMembership(user, storeSlug)) return true;
  const trees = permissionTrees(user, storeSlug);
  return (paths || []).some((path) => trees.some((tree) => isAllowed(tree, path)));
}

/** Exact actions required for a path, or null when the path is always allowed. */
export function requiredPermissionsForPath(path) {
  if (!path) return null;
  if (path === '/') return ['analytics.general.view'];
  if (ALWAYS_ALLOWED.some((p) => path === p || path.startsWith(`${p}/`))) return null;
  const rule = ROUTE_RULES.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
  return rule ? rule[1] : null;
}

export function isPathAllowed(user, storeSlug, path) {
  if (!isRestrictedStaff(user, storeSlug)) return true;
  const required = requiredPermissionsForPath(path);
  if (required == null) return true;
  return canAny(user, storeSlug, required);
}

export function canAccessPath(user, storeSlug, path) {
  if (!isPackPathAllowed(user, path)) return false;
  return isPathAllowed(user, storeSlug, path);
}

/** First nav destination the staff can actually open — used for redirects. */
export function firstAllowedPath(user, storeSlug) {
  const candidates = [
    '/', '/orders', '/orders/abandoned', '/products', '/customers', '/marketing/bulletins',
    '/analytics/vue-ensemble', '/staff', '/discounts', '/channels/kiostore',
    '/shipping', '/tracking', '/purchases/purchase-orders', '/wallet',
    '/affiliate', '/personal-space',
  ];
  return candidates.find((p) => canAccessPath(user, storeSlug, p)) || '/profile';
}
